import { Clock, DollarSign, ShoppingBag, Star } from "lucide-react";
import React, { useEffect, useState } from "react";

import api from "@/services/api";

interface DashboardData {
  availableTrips: number;
  completedRidesToday: number;
  completedRidesTotal: number;
  currency: string;
  declinedTrips: number;
  hoursOnlineToday: number;
  isOnline: boolean;
  myTrips: number;
  rating: number;
  ratingCount: number;
  recentActivity: Array<{
    amount: number;
    description: string;
    timeAgo: string;
    title: string;
    type: string;
  }>;
  status: string;
  todayEarnings: number;
}

interface DashboardProps {
  // Add props here if needed
}

interface DriverDashboardResponse {
  data: {
    available_trips: number;
    completed_rides_today: number;
    completed_rides_total: number;
    currency: string;
    declined_trips: number;
    hours_online_today: number;
    is_online: boolean;
    my_trips: number;
    rating: number;
    rating_count: number;
    recent_activity: Array<{
      amount: number;
      description: string;
      time_ago: string;
      title: string;
      type: string;
    }>;
    status: string;
    today_earnings: number;
  };
  success: boolean;
}

const Dashboard: React.FC<DashboardProps> = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    availableTrips: 0,
    completedRidesToday: 0,
    completedRidesTotal: 0,
    currency: "ZAR",
    declinedTrips: 0,
    hoursOnlineToday: 0,
    isOnline: false,
    myTrips: 0,
    rating: 4.8,
    ratingCount: 0,
    recentActivity: [],
    status: "offline",
    todayEarnings: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response =
          await api.get<DriverDashboardResponse>(`/driver/dashboard`);

        if (response.data.success) {
          const data = response.data.data;
          setDashboardData({
            availableTrips: data.available_trips,
            completedRidesToday: data.completed_rides_today,
            completedRidesTotal: data.completed_rides_total,
            currency: data.currency,
            declinedTrips: data.declined_trips,
            hoursOnlineToday: data.hours_online_today,
            isOnline: data.is_online,
            myTrips: data.my_trips,
            rating: data.rating,
            ratingCount: data.rating_count,
            recentActivity: data.recent_activity.map((activity) => ({
              amount: activity.amount,
              description: activity.description,
              timeAgo: activity.time_ago,
              title: activity.title,
              type: activity.type,
            })),
            status: data.status,
            todayEarnings: data.today_earnings,
          });
        }
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <div className="min-h-screen p-4 dark:bg-gray-900 sm:p-6 lg:p-8">
      <div className="mb-4 sm:mb-6">
        <h1 className="mb-1.5 text-2xl font-bold text-gray-900 dark:text-gray-100 sm:mb-2 sm:text-3xl">
          Driver Dashboard
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 sm:text-base">
          Welcome to your driver dashboard. Manage your rides and track your
          earnings.
        </p>
      </div>

      {/* Dashboard Stats */}
      <div className="mb-6 grid grid-cols-1 gap-3 sm:mb-8 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4 lg:gap-6">
        <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-5 lg:p-6">
          <div className="flex items-center">
            <div className="rounded-full bg-blue-100 p-2 text-blue-600 dark:bg-blue-900 dark:text-blue-400 sm:p-2.5 lg:p-3">
              <DollarSign className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">
                Today's Earnings
              </p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100 sm:text-2xl">
                {dashboardData.currency}{" "}
                {dashboardData.todayEarnings.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-5 lg:p-6">
          <div className="flex items-center">
            <div className="rounded-full bg-green-100 p-2 text-green-600 dark:bg-green-900 dark:text-green-400 sm:p-2.5 lg:p-3">
              <ShoppingBag className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">
                Completed Rides
              </p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100 sm:text-2xl">
                {dashboardData.completedRidesTotal}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-5 lg:p-6">
          <div className="flex items-center">
            <div className="rounded-full bg-yellow-100 p-2 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-400 sm:p-2.5 lg:p-3">
              <Clock className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">
                Hours Online
              </p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100 sm:text-2xl">
                {dashboardData.hoursOnlineToday.toFixed(1)}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-5 lg:p-6">
          <div className="flex items-center">
            <div className="rounded-full bg-yellow-100 p-2 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-400 sm:p-2.5 lg:p-3">
              <Star className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">
                Average Rating
              </p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100 sm:text-2xl">
                {dashboardData.rating.toFixed(2)}★
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500">
                ({dashboardData.ratingCount}{" "}
                {dashboardData.ratingCount === 1 ? "rating" : "ratings"})
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Overview */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:mb-8 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
        <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-5 lg:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                My Trips
              </p>
              <p className="mt-2 text-2xl font-bold text-green-600 dark:text-green-400">
                {dashboardData.myTrips}
              </p>
            </div>
            <div className="rounded-full bg-green-100 p-3 text-green-600 dark:bg-green-900 dark:text-green-400">
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-5 lg:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Declined Trips
              </p>
              <p className="mt-2 text-2xl font-bold text-red-600 dark:text-red-400">
                {dashboardData.declinedTrips}
              </p>
            </div>
            <div className="rounded-full bg-red-100 p-3 text-red-600 dark:bg-red-900 dark:text-red-400">
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M6 18L18 6M6 6l12 12"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-5 lg:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Status
              </p>
              <div className="mt-2 flex items-center">
                <div
                  className={`mr-2 h-3 w-3 rounded-full ${
                    dashboardData.isOnline ? "bg-green-500" : "bg-gray-400"
                  }`}
                ></div>
                <p className="text-lg font-semibold capitalize text-gray-900 dark:text-gray-100">
                  {dashboardData.status}
                </p>
              </div>
            </div>
            <div
              className={`rounded-full p-3 ${
                dashboardData.isOnline
                  ? "bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400"
                  : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
              }`}
            >
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="rounded-lg bg-white shadow-md dark:bg-gray-800">
        <div className="border-b border-gray-200 p-4 dark:border-gray-700 sm:p-5 lg:p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 sm:text-xl">
            Recent Activity
          </h2>
        </div>
        <div className="p-4 sm:p-5 lg:p-6">
          <div className="space-y-4">
            {dashboardData.recentActivity.length > 0 ? (
              dashboardData.recentActivity.map((activity, idx) => {
                const isCompleted = activity.type === "ride_completed";
                const bgColor = isCompleted
                  ? "bg-green-100 dark:bg-green-900"
                  : "bg-blue-100 dark:bg-blue-900";
                const textColor = isCompleted
                  ? "text-green-600"
                  : "text-blue-600";

                return (
                  <div
                    className="flex items-center justify-between border-b border-gray-100 py-3 dark:border-gray-700"
                    key={idx}
                  >
                    <div className="flex items-center">
                      <div
                        className={`flex h-10 w-10 items-center justify-center rounded-full ${bgColor}`}
                      >
                        {isCompleted ? (
                          <svg
                            className={`h-5 w-5 ${textColor}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              d="M5 13l4 4L19 7"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                            />
                          </svg>
                        ) : (
                          <svg
                            className={`h-5 w-5 ${textColor}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              d="M13 10V3L4 14h7v7l9-11h-7z"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                            />
                          </svg>
                        )}
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {activity.title}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {activity.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {dashboardData.currency}{" "}
                        {activity.amount.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {activity.timeAgo}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="py-8 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No recent activity
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
